import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Search, Phone } from "lucide-react";
import { SUBURBS, BUSINESS } from "@/data/site";
import { SectionHeader, Reveal, EASE } from "@/components/motion/Reveal";

const MAP_SRC = "https://maps.google.com/maps?q=Flora%20Hill%2C%20Bendigo%20VIC%203550&t=&z=11&ie=UTF8&iwloc=&output=embed";

export const ServiceArea = () => {
  const [q, setQ] = useState("");
  const list = useMemo(() => SUBURBS.filter((s) => s.toLowerCase().includes(q.trim().toLowerCase())), [q]);

  return (
    <section id="areas" data-testid="areas-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeader
          id="areas"
          eyebrow="Service area"
          lines={["Based in Flora Hill.", <>Covering all of <span className="text-brand-light">Bendigo.</span></>]}
          right="If you can see the Sacred Heart Cathedral spire from your roof, you're in range. A bit further out? Call — we travel."
        />
        <div className="grid lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-7 relative rounded-xl overflow-hidden border border-white/10 bg-panel min-h-[380px]">
            <iframe
              title="Big Rickie's service area map — Bendigo VIC"
              src={MAP_SRC}
              className="absolute inset-0 w-full h-full grayscale-[0.7] contrast-[1.05] invert-[0.92] hue-rotate-180 opacity-90"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="service-area-map"
            />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            <div className="absolute left-5 bottom-5 flex items-center gap-3 bg-ink/90 backdrop-blur border border-white/10 rounded-lg px-4 py-3">
              <span className="relative grid place-items-center w-9 h-9 rounded-full bg-brand text-white">
                <MapPin size={16} strokeWidth={2.5} />
                <span className="absolute inset-0 rounded-full bg-brand animate-pulse-ring" />
              </span>
              <div>
                <div className="font-display font-extrabold uppercase text-white leading-none">Home base</div>
                <div className="text-xs text-slate-400">{BUSINESS.location}</div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5 bg-panel/60 border border-white/10 rounded-xl p-6 sm:p-8 flex flex-col">
            <label className="relative block mb-6">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search your suburb…"
                className="field !pl-11"
                data-testid="suburb-search-input"
              />
            </label>
            <div className="flex flex-wrap gap-2 flex-1 content-start" data-testid="suburb-list">
              <AnimatePresence mode="popLayout">
                {list.map((s) => (
                  <motion.span
                    key={s}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.25, ease: EASE }}
                    className="px-3 py-1.5 rounded-full border border-white/10 bg-ink/60 text-sm text-slate-300 hover:border-brand/60 hover:text-white transition-colors cursor-default"
                    data-testid={`suburb-chip-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {s}
                  </motion.span>
                ))}
              </AnimatePresence>
              {list.length === 0 && (
                <div className="w-full text-sm text-slate-400 py-4" data-testid="suburb-no-match">
                  Don't see <span className="text-white font-semibold">“{q}”</span>? We travel across Central Vic — give us a call and we'll sort it.
                </div>
              )}
            </div>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="btn-primary mt-8 w-full" data-testid="areas-call-btn">
              <Phone size={18} strokeWidth={2.5} /> {BUSINESS.phone}
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
